/**
 * tenantContext.tsx — the portal's single source of "who is signed in and what can they do"
 * (Doc §5.2 step 5: no feature page renders until every request knows tenantId, actorId, role,
 * scopes, environment). Provides the configured `CvsiPortalClient` and the `CustomerContext`.
 */

import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { CvsiPortalClient, scopesForRole } from '@cvsi/shared';
import type { CustomerContext } from '@cvsi/shared';
import { loadSession, saveSession, clearSession, type PortalSession } from './auth';

interface TenantContextValue {
  session: PortalSession | null;
  context: CustomerContext | null;
  client: CvsiPortalClient | null;
  companyName: string | null;
  signIn: (s: PortalSession) => void;
  signOut: () => void;
}

const Ctx = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<PortalSession | null>(() => loadSession());

  const signIn = useCallback((s: PortalSession) => {
    saveSession(s);
    setSession(s);
  }, []);

  const signOut = useCallback(() => {
    clearSession();
    setSession(null);
  }, []);

  const client = useMemo(() => {
    if (!session) return null;
    return new CvsiPortalClient({ baseUrl: session.baseUrl, tokenProvider: async () => session.token });
  }, [session]);

  const [company, setCompany] = useState<{ client: CvsiPortalClient; name: string } | null>(null);
  useEffect(() => {
    let active = true;
    if (client) client.getTenant().then((tenant) => {
      if (active) setCompany({ client, name: tenant.name });
    }).catch(() => { /* Leave references unprefixed when the company cannot be resolved. */ });
    return () => { active = false; };
  }, [client]);
  const companyName = company?.client === client ? company?.name ?? null : null;

  const context = useMemo<CustomerContext | null>(() => {
    if (!session) return null;
    return {
      tenantId: 'self', // resolved server-side from the token; the overview call returns the real id
      actorId: `portal:${session.role}`,
      role: session.role,
      scopes: new Set(scopesForRole(session.role)),
      environment: session.environment,
    };
  }, [session]);

  const value = useMemo(
    () => ({ session, context, client, companyName, signIn, signOut }),
    [session, context, client, companyName, signIn, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTenant(): TenantContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTenant must be used within <TenantProvider>');
  return v;
}

/** Convenience: the client, guaranteed non-null inside authenticated routes. */
export function useClient(): CvsiPortalClient {
  const { client } = useTenant();
  if (!client) throw new Error('No client — route is not authenticated');
  return client;
}
